<script setup>
import { onMounted, ref, watch } from 'vue';
import IconSearch from '../../../icons/IconSearch.vue';
import IconClose from '../../../icons/IconClose.vue';

const props = defineProps({
  label: String,
  model: {
    type: String,
    required: true,
  },
  name: String,
  placeholder: String,
  minLength: {
    type: Number,
    default: 1,
  },
  maxLength: Number,
  errorMessage: {
    type: String,
    default: 'Cannot be blank',
  },
  backgroundColor: String,
  info: String,
  compact: Boolean,
  hasSearchIcon: Boolean,
  hasTrailingIcon: Boolean,
  disabled: Boolean,
  required: Boolean,
  focused: Boolean,
  hasRequiredSign: Boolean,
});

const emit = defineEmits(['input', 'change', 'update:modelValue']);
const inputEl = ref(null);
const localModel = ref(props.model || '');
const isFocused = ref(props.focused);

const handleInput = (e) => {
  emit('input');
  emit('update:modelValue', e.target.value);
};

const handleFocus = () => {
  if (props.disabled) return;
  isFocused.value = true;
};

const handleBlur = () => {
  isFocused.value = false;
};

const handleChange = (e) => {
  emit('change', e);
};

const handleClearInput = () => {
  localModel.value = '';
  emit('update:modelValue', '');
};

watch(() => props.focused, (newValue) => {
  isFocused.value = newValue;
  if (isFocused.value) {
    inputEl.value.focus();
  }
});

watch(() => props.model, (newValue) => {
  localModel.value = newValue;
});

onMounted(() => {
  if (props.focused) {
    inputEl.value.focus();
  }
});
</script>

<template>
  <div class="d-container">
    <label
      :class="disabled ? 'disabled' : undefined"
      @focus="handleFocus"
    >
      <span
        v-if="label"
        :class="[
          isFocused ? 'focused' : undefined,
          required && localModel?.length < minLength && isFocused ? 'error' : undefined
        ]"
        :style="{ backgroundColor: backgroundColor ? backgroundColor : 'inherit' }"
      >
        {{ label }}
        <span v-if="required && hasRequiredSign">*</span>
      </span>
  
      <div
        class="input-container"
        :class="[
          required && localModel?.length < minLength && isFocused ? 'error' : undefined,
          isFocused ? 'focused' : undefined,
          disabled ? 'disabled' : undefined,
          compact ? 'compact' : undefined
        ]"
        @focus="handleFocus"
      >
        <span v-if="hasSearchIcon" class="pge-icon">
          <icon-search />
        </span>
  
        <input
          ref="inputEl"
          type="text"
          v-model="localModel"
          :name="name"
          :placeholder="placeholder"
          :disabled="disabled"
          :maxlength="maxLength"
          :required="required"
          :class="[required && localModel?.length < minLength && isFocused ? 'error' : undefined]"
          @input="handleInput"
          @focus="handleFocus"
          @blur="handleBlur"
          @change="handleChange"
        />
  
        <span
          v-if="hasTrailingIcon && localModel?.length"
          role="button"
          aria-labelledby="Clear Input"
          class="pge-icon clear"
          @click="handleClearInput"
        >
          <icon-close color="#49454F" />
        </span>
      </div>
    </label>

    <div class="d-container__description">
      <div class="d-container__description__info">
        <div class="d-container__description__info__inner">
          <span
            v-if="required && isFocused && localModel?.length < minLength"
            class="error"
          >
            {{ errorMessage }}
          </span>
          <span
            v-if="maxLength && isFocused"
            :class="required && localModel?.length < minLength ? 'error' : undefined"
          >
            {{ localModel?.length }} / {{ maxLength }} Characters Maximum
          </span>
    
          <span v-else-if="maxLength && !isFocused">{{ maxLength }} Characters Maximum</span>
          <span v-else-if="info">{{ info }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.d-container {
  display: flex;
  flex-direction: column;
  row-gap: 4px;
  background-color: inherit;
  width: 100%;

  label {
    position: relative;
    background-color: inherit;
  
    & div {
      display: flex;
      flex-direction: column;
    }
  
    & .pge-icon {
      display: flex;
      align-items: center;
    }
  
    & .pge-icon.clear {
      cursor: pointer;
    }
  
    & input {
      background-color: transparent;
      border: none;
      outline: none;
      font-size: 16px;
      width: 100%;
      caret-color: #5E2F78;
    }
  
    & .input-container {
      display: flex;
      flex-direction: row;
      column-gap: 8px;
      background-color: transparent;
      border: 1px solid #79747E;
      border-radius: 4px;
      height: 56px;
      padding: 12px;
      cursor: text;
    }
  
    & .input-container.compact {
      height: 40px !important;
      padding: 6px 12px !important;
    }
  
    & .input-container.disabled {
      cursor: not-allowed;
    }
  
    & .input-container.focused {
      border: 3px solid #5E2F78;
      padding: 12px 10px;
    }
  
    & .input-container.focused.compact {
      padding: 6px 10px !important;
    }
  
    & .input-container.error {
      border: 3px solid #B3261E;
      padding: 12px 10px;

      input {
        caret-color: #B3261E;
      }
    }
  
    & .input-container.error.compact {
      padding: 6px 10px !important;
    }
    
  }
  
  label > span {
    font-size: 12px;
    line-height: 16px;
    color: #49454F;
  }
  
  label > span:first-of-type {
    position: absolute;
    top: -7px;
    left: 12px;
    z-index: 1;
    padding: 0 4px;
    background-color: inherit;
    font-size: 12px;
    line-height: 16px;
  }
  
  label span:first-of-type.focused {
    color: #5E2F78 !important;
  
    & span {
      color: #5E2F78 !important;
    }
  }
  
  label span:first-of-type.error {
    color: #B3261E !important;
  
    & span {
      color: #B3261E !important;
    }
  }
  
  label.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  
    & input {
      cursor: not-allowed;
    }
  }

  &__description {
    display: flex;
    flex-direction: column;
    font-size: 12px;
    line-height: 16px;
    color: #49454F;

    & .error {
      color: #B3261E;
    }

    &__info {
      position: relative;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;

      &__inner {
        display: flex;
        flex-direction: column;
      }
    }
  }
}
</style>
